import React from "react";
import { Button as UIButton } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart3,
  Settings,
  Activity,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { buildAdminUrl } from "@/utils/admin-config";

export const DashboardNavigation: React.FC = () => {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Analytics Dashboard Options
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <BarChart3 className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="font-semibold">Enhanced Dashboard</h3>
                <p className="text-sm text-gray-600">
                  Fixed layout with comprehensive analytics
                </p>
              </div>
            </div>
            <UIButton asChild variant="outline" size="sm" className="w-full">
              <a href={buildAdminUrl("/analytics")}>
                View Enhanced
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </UIButton>
          </div>

          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="h-6 w-6 text-purple-600" />
              <div>
                <h3 className="font-semibold">Customizable Dashboard</h3>
                <p className="text-sm text-gray-600">
                  Drag & drop, resize, personalize layout
                </p>
              </div>
            </div>
            <UIButton
              asChild
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              size="sm"
            >
              <a href={buildAdminUrl("/analytics/custom")}>
                Customize Now
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </UIButton>
          </div>

          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <BarChart3 className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="font-semibold">Basic Dashboard</h3>
                <p className="text-sm text-gray-600">
                  Simple overview and basic metrics
                </p>
              </div>
            </div>
            <UIButton asChild variant="outline" size="sm" className="w-full">
              <a href={buildAdminUrl("/analytics/basic")}>
                View Basic
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </UIButton>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-900 mb-2">
            🎉 Complete: Advanced Analytics Dashboard
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-800">
            <div>
              <strong>Customization Features:</strong>
              <ul className="ml-2 space-y-0.5">
                <li>• Drag & drop widget layouts</li>
                <li>• Resize widgets dynamically</li>
                <li>• Show/hide any widget</li>
                <li>• Auto-save preferences</li>
                <li>• Reset to defaults</li>
                <li>• Responsive design</li>
              </ul>
            </div>
            <div>
              <strong>Analytics Widgets:</strong>
              <ul className="ml-2 space-y-0.5">
                <li>• Real-time visitor tracking</li>
                <li>• Device analytics table</li>
                <li>• Popular pages insights</li>
                <li>• User journey analysis</li>
                <li>• UTM source breakdown</li>
                <li>• Location & keyword data</li>
              </ul>
            </div>
          </div>
          <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700 text-center">
            💡 Over 15 customizable widgets • Zero-cost solution •
            Production-ready
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardNavigation;
